import { lazy, Suspense } from "react";
import { Switch, Route, Router as WouterRouter } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import NotFound from "@/pages/not-found";
import Home from "@/pages/Home";
import AuthPage from "@/pages/AuthPage";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import CheckoutCancel from "@/pages/CheckoutCancel";
import Worlds from "@/pages/Worlds";
import Marketplace from "@/pages/Marketplace";
import ProductDetailPage from "@/pages/ProductDetailPage";
import Avatars from "@/pages/Avatars";
import AvatarDetail from "@/pages/AvatarDetail";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import SpaceDetail from "@/pages/SpaceDetail";
import IllusionistzProfile from "@/pages/IllusionistzProfile";
import EmeraldOnyxProfile from "@/pages/EmeraldOnyxProfile";
import MistyHazeProfile from "@/pages/MistyHazeProfile";
import ClaudiaGProfile from "@/pages/ClaudiaGProfile";
import AVTheEntertainerProfile from "@/pages/AVTheEntertainerProfile";
import GermaniProfile from "@/pages/GermaniProfile";
import MsTProfile from "@/pages/MsTProfile";
import GoldieProfile from "@/pages/GoldieProfile";
import RhythmAndSoulPatrolProfile from "@/pages/RhythmAndSoulPatrolProfile";
import CreatorProfile from "@/pages/CreatorProfile";
import ContentGallery from "@/pages/ContentGallery";
import CreatorsHub from "@/pages/CreatorsHub";
import PostPage from "@/pages/PostPage";
import WorldMap from "@/pages/WorldMap";
import MapPlacement from "@/pages/MapPlacement";
import UrbanFilmFestival from "@/pages/UrbanFilmFestival";
import UrbanFilmFestivalDate from "@/pages/UrbanFilmFestival/DateSchedule";
import Navbar from "@/components/layout/Navbar";
import ScrollToTop from "@/components/ScrollToTop";

/* Lazy-load pages that pull in Three.js — keeps the main bundle clean
   and prevents module-level Three.js init from crashing at startup.
   On import failure (e.g. stale browser module cache), reload once to
   clear the module registry automatically. */
const _3dReload = (e: unknown) => {
  if (!sessionStorage.getItem("_3dr")) {
    sessionStorage.setItem("_3dr", "1");
    location.reload();
  }
  throw e;
};
const WorldViewer  = lazy(() => import("@/pages/WorldViewer").catch(_3dReload));
const NavyPierVenue = lazy(() => import("@/pages/NavyPierVenue").catch(_3dReload));

const queryClient = new QueryClient();

function ThreeFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh] text-muted-foreground text-sm">
      Loading 3D viewer…
    </div>
  );
}

function Router() {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col font-sans dark">
      <ScrollToTop />
      <Navbar />
      <main className="flex-1 flex flex-col">
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/worlds" component={Worlds} />
          <Route path="/marketplace/:id" component={ProductDetailPage} />
          <Route path="/marketplace" component={Marketplace} />
          <Route path="/avatars" component={Avatars} />
          <Route path="/avatars/:id" component={AvatarDetail} />
          <Route path="/events" component={Events} />
          <Route path="/events/:id" component={EventDetail} />
          <Route path="/spaces/:id" component={SpaceDetail} />
          <Route path="/viewer">
            <Suspense fallback={<ThreeFallback />}>
              <WorldViewer />
            </Suspense>
          </Route>
          <Route path="/navy-pier">
            <Suspense fallback={<ThreeFallback />}>
              <NavyPierVenue />
            </Suspense>
          </Route>
          <Route path="/world-map" component={WorldMap} />
          <Route path="/map-placement" component={MapPlacement} />
          <Route path="/map-placement/success" component={MapPlacement} />
          <Route path="/urban-film-festival-miami" component={UrbanFilmFestival} />
          <Route path="/urban-film-festival-miami/:date" component={UrbanFilmFestivalDate} />
          <Route path="/illusionists" component={IllusionistzProfile} />
          <Route path="/creators/illusionistz" component={IllusionistzProfile} />
          <Route path="/creators/emeraldonyx" component={EmeraldOnyxProfile} />
          <Route path="/creators/mistyhaze" component={MistyHazeProfile} />
          <Route path="/creators/claudiag" component={ClaudiaGProfile} />
          <Route path="/creators/avtheentertainer" component={AVTheEntertainerProfile} />
          <Route path="/creators/germani" component={GermaniProfile} />
          <Route path="/creators/mst" component={MsTProfile} />
          <Route path="/creators/goldie" component={GoldieProfile} />
          <Route path="/creators/rhythmandsoulpatrol" component={RhythmAndSoulPatrolProfile} />
          <Route path="/creators/joerussketchbook">
            <CreatorProfile
              creatorId={19}
              categoryLabel="Artist"
              routeSlug="joerussketchbook"
              tags={["Jean Grey", "The Phoenix", "Kate Bishop", "Catwoman", "Sketchbook"]}
            />
          </Route>
          <Route path="/creators/@joerussketchbook">
            <CreatorProfile
              creatorId={19}
              categoryLabel="Artist"
              routeSlug="joerussketchbook"
              tags={["Jean Grey", "The Phoenix", "Kate Bishop", "Catwoman", "Sketchbook"]}
            />
          </Route>
          <Route path="/creators/flawlesslounge">
            <CreatorProfile
              creatorId={20}
              categoryLabel="Beauty Channel"
              routeSlug="flawlesslounge"
              tags={["Holistic Haircare", "Beauty Business", "Tutorials", "Events", "Promotional Media"]}
            />
          </Route>
          <Route path="/content" component={ContentGallery} />
          <Route path="/creators/:slug/post/:id" component={PostPage} />
          <Route path="/creators" component={CreatorsHub} />
          <Route path="/auth" component={AuthPage} />
          <Route path="/checkout/success" component={CheckoutSuccess} />
          <Route path="/checkout/cancel" component={CheckoutCancel} />
          <Route component={NotFound} />
        </Switch>
      </main>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={
          // Runtime detection: if the page is served under /ism-new-world (Replit external proxy),
          // use that as the base so Links don't drop the prefix on navigation.
          // At root (internal dev / screenshot tool), the prefix is absent and base stays "".
          window.location.pathname.startsWith("/ism-new-world") ? "/ism-new-world" : ""
        }>
          <AuthProvider>
            <Router />
          </AuthProvider>
        </WouterRouter>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
